import { useEffect, useState, SyntheticEvent } from 'react';

import { useRecoilValue, useRecoilValueLoadable } from 'recoil';

import { styled } from '@mui/material/styles';

import ToggleButtons from 'src/components/Button/ToggleButton';
import FilterPanel from 'src/components/FilterPanel';
import DropdownMenu from 'src/components/Input/DropdownMenu';
import { datasetState } from 'src/states/dataset';
import { myModalitiesWithoutDeprecatedCCT } from 'src/states/myInfo';
import { CaseSearchQueryConfig } from 'src/types/api/data/case';
import { ProjectCreateSchema } from 'src/types/api/data/project';
import { SelectOption } from 'src/types/client/ui';

interface CaseSearchQueryFormProps {
  onSubmit: (value: CaseSearchQueryConfig) => void;
  onChangeModality?: (opt: SelectOption) => void;
  isModalityChangeable?: boolean;
  isSearching?: boolean;
  modality: ProjectCreateSchema['modality'];
  projectId?: string;
}

export default function CaseSearchQueryForm({
  onSubmit,
  modality,
  isModalityChangeable,
  onChangeModality = () => {},
  projectId,
}: CaseSearchQueryFormProps): JSX.Element {
  const myModalityOptionList = useRecoilValue(myModalitiesWithoutDeprecatedCCT);

  const myModalityOptions = !isModalityChangeable
    ? myModalityOptionList.filter(option => option.label === modality)
    : myModalityOptionList;

  const [selectedModality, setSelectedModality] = useState<SelectOption>();
  const [selectedDataset, setSelectedDataset] = useState<SelectOption>();

  const datasetOptsLoadable = useRecoilValueLoadable(
    datasetState.allDatasetOptionList({
      modality,
      projectId,
    })
  );
  const { state, contents } = datasetOptsLoadable;
  const datasetOpts = state === 'hasValue' ? contents : [];

  const handleChangeModality = (opt: SelectOption) => {
    setSelectedModality(opt);
    onChangeModality(opt);

    if (!!opt) {
      setSelectedDataset(undefined);
    }
  };

  const handleChangeDataset = (
    event: SyntheticEvent,
    opt: SelectOption | null
  ) => {
    setSelectedDataset(opt || undefined);

    const currentModalityLabel = selectedModality?.label || modality;
    const payload: CaseSearchQueryConfig = {
      modality: currentModalityLabel || '',
      datasetName: opt?.label,
      datasetId: opt?.value,
      readyForJobOnly: true,
      projectId,
    };

    onSubmit(payload);
  };

  useEffect(() => {
    setSelectedModality(() => {
      if (modality) {
        return myModalityOptions.find(opt => opt.label === modality);
      } else {
        return myModalityOptions[0];
      }
    });
  }, [modality, myModalityOptions]);

  return (
    <Form>
      <FilterPanel>
        <ToggleButtons
          options={myModalityOptions}
          value={selectedModality || null}
          onChangeLabel={handleChangeModality}
        />
        <DropdownMenu
          options={datasetOpts}
          label="Select a dataset"
          onChange={handleChangeDataset}
          value={selectedDataset}
          disabled={state === 'loading'}
          loading={state === 'loading'}
          sx={{ minWidth: '20rem' }}
        />
      </FilterPanel>
    </Form>
  );
}

const Form = styled('form')`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  width: auto;
`;
